'use client'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { SongArtwork } from '@/components/ui/song-artwork'
import {
  Music,
  ThumbsUp,
  DollarSign,
  X,
  ChevronDown,
  ListMusic,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { DemoQueueRequest, DemoEventSettings } from '../demo-types'

interface QueueSectionProps {
  queue: DemoQueueRequest[]
  settings: DemoEventSettings
  visitorUpvotedRequestIds: Set<string>
  onUpvote: (request: DemoQueueRequest) => void
  onBoost: (request: DemoQueueRequest) => void
  onCancelBoost: (requestId: string) => void
}

export function QueueSection({
  queue,
  settings,
  visitorUpvotedRequestIds,
  onUpvote,
  onBoost,
  onCancelBoost,
}: QueueSectionProps) {
  const t = useTranslations('demo')
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <ListMusic className="h-4 w-4 text-zinc-400" />
        <span className="font-semibold text-white">{t('songQueue')}</span>
        {queue.length > 0 && (
          <Badge variant="secondary" className="text-xs">{queue.length}</Badge>
        )}
      </div>

      {queue.length === 0 ? (
        <Card className="border-zinc-800">
          <CardContent className="py-8 text-center">
            <Music className="h-10 w-10 mx-auto mb-3 text-zinc-600" />
            <p className="text-sm text-zinc-500">{t('noRequestsYet')}</p>
            <p className="text-xs text-zinc-600 mt-1">{t('beFirstToRequest')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {queue.slice(0, 10).map((request, index) => (
            <Card key={request.id} className={`overflow-hidden relative ${
              request.isFromVisitor
                ? 'bg-gradient-to-r from-violet-600/30 to-fuchsia-600/30 border-violet-500/50'
                : 'bg-zinc-800/50 border-zinc-700'
            }`}>
              <CardContent className="px-3 pt-3 pb-2">
                {/* Floating badges in top right */}
                {(request.totalTipsCents > 0 || request.upvotes > 0) && (
                  <div className="absolute top-2 right-2 flex items-center gap-1">
                    {request.totalTipsCents > 0 && (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px] px-1.5 py-0.5">
                        <DollarSign className="h-2.5 w-2.5 mr-0.5" />
                        {(request.totalTipsCents / 100).toFixed(0)}
                      </Badge>
                    )}
                    <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 text-[10px] px-1.5 py-0.5">
                      <ThumbsUp className="h-2.5 w-2.5 mr-0.5" />
                      {request.upvotes}
                    </Badge>
                  </div>
                )}

                {/* Main row: position, artwork, song info */}
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold w-6 text-center text-zinc-600 shrink-0">
                    {index + 1}
                  </span>
                  <SongArtwork
                    src={request.song.artworkUrl}
                    alt={request.song.title}
                    size={48}
                    className="h-12 w-12 rounded-md shrink-0"
                  />
                  <div className="flex-1 min-w-0 pr-16">
                    <p className="font-medium text-white text-base leading-tight truncate">{request.song.title}</p>
                    <p className="text-sm text-zinc-400 truncate">{request.song.artist}</p>
                    {request.isFromVisitor && (
                      <p className="text-xs text-violet-400 mt-0.5">&#x2728; {t('yourRequest')}</p>
                    )}
                  </div>
                </div>

                {/* Actions row */}
                {(!visitorUpvotedRequestIds.has(request.id) || request.visitorBoostCents > 0 || settings.tipsEnabled) && (
                  <div className="flex items-center justify-between gap-2 mt-1.5">
                    {/* Upvote button on left */}
                    <div>
                      {!visitorUpvotedRequestIds.has(request.id) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onUpvote(request)}
                          className="text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 h-7 px-2"
                        >
                          <ThumbsUp className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>

                    {/* Action buttons - right side */}
                    <div className="flex items-center gap-1.5 ml-auto">
                      {/* Visitor's own boost */}
                      {request.visitorBoostCents > 0 && (
                        <>
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
                            {t('yourBoost', { amount: (request.visitorBoostCents / 100).toFixed(0) })}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onCancelBoost(request.id)}
                            className="border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-400 h-6 w-6 p-0"
                            title={t('cancelBoost')}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </>
                      )}

                      {/* Boost button */}
                      {settings.tipsEnabled && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onBoost(request)}
                          className="border-green-500/30 bg-green-500/10 hover:bg-green-500/20 text-green-400 h-7 px-2.5 text-xs"
                        >
                          <DollarSign className="h-3 w-3 mr-1" />
                          {request.visitorBoostCents > 0 ? t('boostMore') : t('boost')}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {queue.length > 10 && (
            <Button
              variant="outline"
              className="w-full border-violet-500/30 text-violet-400 hover:bg-violet-500/10 hover:text-violet-300 hover:border-violet-500/50"
              onClick={() => {/* Could open a modal for all requests */}}
            >
              <ChevronDown className="h-4 w-4 mr-2" />
              {queue.length - 10 !== 1 ? t('viewMoreRequestsPlural', { count: queue.length - 10 }) : t('viewMoreRequests', { count: queue.length - 10 })}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
